import type { RouteConfig } from '@/config/routeConfig';
import { ENTITY_GROUPS } from '@/components/customizing/EntityConfigs';
import { canShowEntityInSidebar, getEntityNavPrivileges } from '@/lib/customizing/entityAccess';
import type { EntityType } from '@/types/customizing';
import type { PrivilegeEnum } from '@/api/ApiClient';
import type { Operation, PrivilegeWithOperation } from '@/stores/authStore';

/**
 * Flat Customizing submenu entries (same shape as Monitors / Reports / Config).
 * Grouping stays in the in-page sidebar, not the hamburger.
 */
export function getCustomizingSubRoutes(): RouteConfig[] {
  return ENTITY_GROUPS.flatMap((group) =>
    group.entities.map(({ key, config }) => ({
      label: config.title,
      path: `/customizing/${key}`,
      privileges: getEntityNavPrivileges(config),
    }))
  );
}

/**
 * Parent-item privileges: any child privilege (Monitors-style union).
 */
export function getCustomizingParentPrivileges(): PrivilegeWithOperation[] {
  const seen = new Set<string>();
  const privileges: PrivilegeWithOperation[] = [];
  for (const route of getCustomizingSubRoutes()) {
    for (const privilege of route.privileges ?? []) {
      const key = `${privilege.p}:${privilege.op ?? 'read'}`;
      if (!seen.has(key)) {
        seen.add(key);
        privileges.push(privilege);
      }
    }
  }
  return privileges;
}

/**
 * First customizing entity the user may see, in catalog order (not Users-first).
 * Returns null when none are available.
 */
export function getFirstAccessibleCustomizingEntity(
  isAuthorized: (privilege: PrivilegeEnum, op: Operation) => boolean
): EntityType | null {
  for (const group of ENTITY_GROUPS) {
    for (const { key, config } of group.entities) {
      if (canShowEntityInSidebar(config, isAuthorized)) {
        return key;
      }
    }
  }
  return null;
}
